import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Camera,
  Upload,
  Loader2, 
  CheckCircle2,
  MapPin,
  Navigation,
  X,
  Image,
  Shield,
  Trash2
} from 'lucide-react';

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-blue-400' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-400' },
  { value: 'high', label: 'High', color: 'text-orange-400' },
  { value: 'critical', label: 'Critical', color: 'text-red-400' },
];

const CATEGORY_OPTIONS = [
  { value: 'security_breach', label: 'Security Breach' },
  { value: 'theft', label: 'Theft' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'trespassing', label: 'Trespassing' },
  { value: 'fire', label: 'Fire' },
  { value: 'medical', label: 'Medical Emergency' },
  { value: 'gate_open', label: 'Gate Open' },
  { value: 'equipment_failure', label: 'Equipment Failure' },
  { value: 'suspicious_activity', label: 'Suspicious Activity' },
  { value: 'accident', label: 'Accident' },
  { value: 'other', label: 'Other' },
];

const MAX_PHOTOS = 5;
const MAX_PHOTO_SIZE_MB = 5;

export default function ReportIncidentModal({ 
  isOpen, 
  onClose, 
  patrol, 
  api, 
  onIncidentCreated,
  checkpoint = null 
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium',
    category: 'other',
    location_description: '',
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('pending');
  const [gpsCoords, setGpsCoords] = useState(null);
  const [createdIncident, setCreatedIncident] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Capture GPS when modal opens
  useEffect(() => {
    if (isOpen) {
      captureGPS();
      // Reset form
      setFormData({
        title: '',
        description: '',
        severity: 'medium',
        category: 'other',
        location_description: checkpoint?.name ? `Near ${checkpoint.name}` : '',
      });
      setPhotos([]);
      setCreatedIncident(null);
    }
  }, [isOpen, checkpoint]);

  const captureGPS = () => {
    setGpsStatus('capturing');
    
    if (!navigator.geolocation) {
      setGpsStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_meters: position.coords.accuracy,
          captured_at: new Date().toISOString(),
        });
        setGpsStatus('captured');
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsStatus('error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleFileSelect = async (e, isCamera = false) => {
    const files = Array.from(e.target.files || []);
    
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files allowed');
        continue;
      }
      
      if (file.size > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
        toast.error(`Photo must be under ${MAX_PHOTO_SIZE_MB}MB`);
        continue;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, {
          id: `temp-${Date.now()}-${Math.random()}`,
          file: file,
          preview: e.target.result,
          name: file.name,
          caption: '',
          isCamera,
        }]);
      };
      reader.readAsDataURL(file);
    }

    // Reset input
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const updatePhotoCaption = (photoId, caption) => {
    setPhotos(prev => prev.map(p =>
      p.id === photoId ? { ...p, caption } : p
    ));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    if (!gpsCoords) {
      toast.warning('GPS location has not been captured. Please allow location access and wait a few seconds before submitting.');
    }

    if (photos.some(p => !p.caption || !p.caption.trim())) {
      toast.error('Please enter a caption for each photo');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create incident first
      const incidentPayload = {
        site_id: patrol?.site_id || null,
        site_name: patrol?.site_name || patrol?.site?.name || null,
        title: formData.title.trim(),
        description: formData.description.trim(),
        severity: formData.severity,
        category: formData.category,
        occurred_at: new Date().toISOString(),
        location_description: formData.location_description.trim() || null,
        patrol_run_id: patrol?.id || patrol?._id || null,
        checkpoint_id: checkpoint?.id || checkpoint?._id || null,
        checkpoint_name: checkpoint?.name || checkpoint?.sector_name || checkpoint?.label || null,

        gps: finalGpsCoords ? {
          latitude: finalGpsCoords.latitude,
          longitude: finalGpsCoords.longitude,
          accuracy: finalGpsCoords.accuracy || finalGpsCoords.accuracy_m || null,
          captured_at: new Date().toISOString()
        } : null,

        latitude: finalGpsCoords?.latitude || null,
        longitude: finalGpsCoords?.longitude || null,
        gps_lat: finalGpsCoords?.latitude || null,
        gps_lng: finalGpsCoords?.longitude || null,
        accuracy_m: finalGpsCoords?.accuracy || finalGpsCoords?.accuracy_m || null,

        location: finalGpsCoords
          ? `${finalGpsCoords.latitude}, ${finalGpsCoords.longitude}`
          : null,
      };

      const response = await api.post('/incidents', incidentPayload);
      const incident = response.data;
      
      toast.success(`Incident ${incident.ir_number} created`);
      setCreatedIncident(incident);

      // Upload photos if any
      if (photos.length > 0) {
        setUploadingPhotos(true);
        
        for (const photo of photos) {
          try {
            const formData = new FormData();
            formData.append('file', photo.file);
            if (photo.caption?.trim()) {
              formData.append('caption', photo.caption.trim());
            }
            
            if (gpsCoords) {
              formData.append('gps_lat', gpsCoords.latitude);
              formData.append('gps_lng', gpsCoords.longitude);
            }
            formData.append('captured_at', new Date().toISOString());

            await api.post(`/incidents/${incident.id}/photos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch (err) {
            console.error('Photo upload error:', err);
            toast.error(`Failed to upload ${photo.name}`);
          }
        }
        
        setUploadingPhotos(false);
        toast.success(`${photos.length} photo(s) uploaded`);
      }

      if (onIncidentCreated) {
        onIncidentCreated(incident);
      }

    } catch (error) {
      console.error('Create incident error:', error);

      const raw = error?.response?.data?.detail;
      let msg;

      if (Array.isArray(raw)) {
        msg = raw
          .map(e => {
            const field = Array.isArray(e?.loc)
              ? e.loc.filter(p => p !== 'body').join('.')
              : '';
            return field
              ? `${field}: ${e?.msg || 'invalid'}`
              : (e?.msg || 'Validation error');
          })
          .join('; ');
      } else if (typeof raw === 'string') {
        msg = raw;
      } else if (raw && typeof raw === 'object') {
        msg = raw.msg || JSON.stringify(raw);
      } else {
        msg = error?.message || 'Failed to create incident';
      }

      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      severity: 'medium',
      category: 'other',
      location_description: '',
    });
    setPhotos([]);
    setGpsCoords(null);
    setGpsStatus('pending');
    setCreatedIncident(null);
    onClose();
  };

  // Success view after incident created
  if (createdIncident) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              Incident Reported
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center">
              <Badge className="text-lg px-4 py-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
                {createdIncident.ir_number}
              </Badge>
            </div>
            
            <div className="bg-[#27272a] rounded-sm p-4 space-y-2">
              <h3 className="font-medium text-white">{createdIncident.title}</h3>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Badge className={`text-xs ${
                  createdIncident.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                  createdIncident.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                  createdIncident.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>
                  {createdIncident.severity.toUpperCase()}
                </Badge>
                <span>{createdIncident.category.replace('_', ' ')}</span>
              </div>
              {createdIncident.photos_count > 0 && (
                <p className="text-xs text-zinc-500 flex items-center gap-1">
                  <Camera className="w-3 h-3" />
                  {createdIncident.photos_count} photo(s) attached
                </p>
              )}
            </div>

            <p className="text-sm text-zinc-400 text-center">
              Linked to patrol: <span className="text-zinc-300">{patrol.route_name}</span>
            </p>
          </div>

          <DialogFooter>
            <Button onClick={handleClose} className="w-full bg-blue-500 hover:bg-blue-600">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Report Incident
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Report an incident during {patrol?.route_name || 'patrol'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4" data-testid="report-incident-modal">
            {/* GPS Status */}
            <div className="flex items-center justify-between p-3 bg-[#27272a] rounded-sm">
              <div className="flex items-center gap-2">
                <Navigation className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-zinc-300">GPS Location</span>
              </div>
              <div>
                {gpsStatus === 'capturing' && (
                  <Badge className="bg-blue-500/20 text-blue-400">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Capturing...
                  </Badge>
                )}
                {gpsStatus === 'captured' && (
                  <Badge className="bg-emerald-500/20 text-emerald-400">
                    <MapPin className="w-3 h-3 mr-1" />
                    Captured
                  </Badge>
                )}
                {gpsStatus === 'error' && (
                  <Badge 
                    className="bg-red-500/20 text-red-400 cursor-pointer" 
                    onClick={captureGPS}
                  >
                    Retry
                  </Badge>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Title <span className="text-red-400">*</span></Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief description of incident"
                className="bg-[#09090b] border-[#3f3f46]"
                disabled={isSubmitting}
                data-testid="incident-title-input"
              />
            </div>

            {/* Severity & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Severity</Label>
                <Select 
                  value={formData.severity} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, severity: v }))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {SEVERITY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className={opt.color}>{opt.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Description <span className="text-red-400">*</span></Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of what happened..."
                className="bg-[#09090b] border-[#3f3f46] min-h-[100px]"
                disabled={isSubmitting}
                data-testid="incident-description-input"
              />
            </div>

            {/* Location Description */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Location Description</Label>
              <Input
                value={formData.location_description}
                onChange={(e) => setFormData(prev => ({ ...prev, location_description: e.target.value }))}
                placeholder="e.g., Near gate 3, Loading dock area"
                className="bg-[#09090b] border-[#3f3f46]"
                disabled={isSubmitting}
              />
            </div>

            {/* Photo Evidence */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Photo Evidence ({photos.length}/{MAX_PHOTOS})</Label>
              
              {/* Photo Grid */}
              {photos.length > 0 && (
                <div className="grid grid-cols-1 gap-3 mb-3">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative group space-y-2">
                      <div className="relative">
                        <img 
                          src={photo.preview} 
                          alt={photo.name}
                          className="w-full h-20 object-cover rounded border border-[#3f3f46]"
                        />

                        <button
                          onClick={() => removePhoto(photo.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={isSubmitting}
                        >
                          <X className="w-3 h-3" />
                        </button>

                        {photo.isCamera && (
                          <Badge className="absolute bottom-1 left-1 text-xs bg-blue-500/80">
                            <Camera className="w-3 h-3" />
                          </Badge>
                        )}
                      </div>

                      <input
                        type="text"
                        value={photo.caption || ''}
                        onChange={(e) => updatePhotoCaption(photo.id, e.target.value)}
                        placeholder="Photo caption"
                        className="w-full rounded border border-[#3f3f46] bg-[#09090b] px-2 py-1 text-xs text-white placeholder:text-zinc-500"
                        disabled={isSubmitting}
                      />

                      <div className="text-[10px] text-zinc-500 truncate">
                        {photo.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Photo Buttons */}
              {photos.length < MAX_PHOTOS && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 border-zinc-600"
                    disabled={isSubmitting}
                    data-testid="take-photo-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 border-zinc-600"
                    disabled={isSubmitting}
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </div>
              )}

              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileSelect(e, true)}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileSelect(e, false)}
                className="hidden"
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 pt-4 border-t border-[#3f3f46]">
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="border-zinc-600"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-red-500 hover:bg-red-600"
            disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
            data-testid="submit-incident-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadingPhotos ? 'Uploading Photos...' : 'Creating...'}
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Report Incident
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
